import { displayStatus, type DisplayStatus, type DomainKey, type InterviewState, type Recommendation, type RecommendationKind, type UnderstandingItem } from './model';

/**
 * Compte rendu d'un entretien, dérivé de son état : la synthèse du client (POC §10.8) et le
 * back-office AYUMIA lisent le même calcul. Aucun texte n'y est inventé : chaque ligne vient d'un
 * fait, d'une recommandation ou d'un calcul de readiness.
 */

export const DOMAIN_LABELS: Readonly<Record<DomainKey, string>> = {
  company: 'Entreprise',
  organization: 'Organisation et responsabilités',
  products: 'Produits',
  customers: 'Clients',
  sales: 'Ventes',
  purchasing: 'Achats',
  inventory: 'Stocks',
  production: 'Production',
  quality: 'Qualité',
  traceability: 'Traçabilité',
  accounting: 'Facturation et comptabilité',
  systems: 'Systèmes existants',
};

export const RECOMMENDATION_LABELS: Readonly<Record<RecommendationKind, string>> = {
  replace: 'Remplacer',
  integrate: 'Intégrer',
  keep: 'Conserver',
  ignore: 'Ignorer',
  review: 'À étudier',
};

export const STATUS_LABELS: Readonly<Record<DisplayStatus, string>> = {
  confirmed: 'Confirmé',
  inferred: 'Déduit',
  to_confirm: 'À confirmer',
  missing: 'Manquant',
  conflicting: 'Contradictoire',
};

export interface ReportLine {
  id: string;
  label: string;
  value: string;
  status: DisplayStatus;
}

export interface InterviewReport {
  company: string;
  /** Faits connus, par domaine, dans l'ordre d'affichage ; les faits remplacés n'y sont pas. */
  sections: Array<{ domain: DomainKey; label: string; lines: ReportLine[] }>;
  organization: ReportLine[];
  proposed_scope: string[];
  systems: Array<{ id: string; current: string; domain: string; strategy: string; decided: boolean }>;
  open_items: Array<{ id: string; label: string; domain: string; deferred: boolean }>;
  open_decisions: string[];
  inferred_to_confirm: ReportLine[];
  readiness: { overall: number; domains: Array<{ label: string; score: number; blocking: string[] }>; blockers: string[] };
}

const ORDER: readonly DomainKey[] = ['company', 'organization', 'products', 'customers', 'sales', 'purchasing', 'inventory', 'production', 'quality', 'traceability', 'accounting', 'systems'];

function strategy(rec: Recommendation): string {
  const proposed = rec.recommendation.map((kind) => RECOMMENDATION_LABELS[kind]).join(' + ');
  if (rec.decision === 'keep_current') return 'Garder l’existant (choix du client)';
  if (rec.decision === 'accepted') return `${proposed} (accepté)`;
  if (rec.decision === 'study_ayumia') return `${proposed}${rec.likely === undefined ? '' : ` → ${RECOMMENDATION_LABELS[rec.likely].toLowerCase()}`} (à étudier avec AYUMIA)`;
  return `${proposed} (proposé, non décidé)`;
}

function line(item: UnderstandingItem): ReportLine {
  return { id: item.id, label: item.label, value: item.value ?? '—', status: displayStatus(item) };
}

export function buildReport(state: InterviewState): InterviewReport {
  const live = state.items.filter((item) => item.validation_status !== 'superseded');
  const known = live.filter((item) => item.value !== undefined && item.validation_status !== 'rejected');
  const sections = ORDER.map((domain) => ({ domain, label: DOMAIN_LABELS[domain], lines: known.filter((item) => item.domain === domain).map(line) })).filter((section) => section.lines.length > 0);
  const scope = state.readiness.domains
    .filter((domain) => domain.score >= 50 && domain.domain !== 'company' && domain.domain !== 'systems' && domain.domain !== 'organization')
    .map((domain) => DOMAIN_LABELS[domain.domain]);
  return {
    company: state.company,
    sections,
    organization: known.filter((item) => item.domain === 'organization' || item.id === 'quality_owner' || item.id === 'quality_approver').map(line),
    proposed_scope: scope,
    systems: state.recommendations.map((rec) => ({ id: rec.id, current: rec.current, domain: DOMAIN_LABELS[rec.domain], strategy: strategy(rec), decided: rec.decision !== undefined && rec.decision !== 'study_ayumia' })),
    open_items: live.filter((item) => item.value === undefined || item.validation_status === 'rejected').map((item) => ({ id: item.id, label: item.label, domain: DOMAIN_LABELS[item.domain], deferred: item.deferred === true })),
    open_decisions: state.recommendations.filter((rec) => rec.decision === undefined || rec.decision === 'study_ayumia').map((rec) => rec.title),
    inferred_to_confirm: known.filter((item) => ['inferred', 'to_confirm', 'conflicting'].includes(displayStatus(item))).map(line),
    readiness: {
      overall: state.readiness.overall,
      domains: state.readiness.domains.map((domain) => ({ label: DOMAIN_LABELS[domain.domain], score: domain.score, blocking: domain.blocking })),
      blockers: state.readiness.blockers,
    },
  };
}
